import type { Filter } from "@/components/StructuredSearch/config/types";
import { FilterValueType } from "@/components/StructuredSearch/config/structured-search-schema";
import { StructuredSearchField } from "@/components/StructuredSearch/config/types";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { createStructuredSearchDefaultDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { stripReviewLogsDrawerIntent, stripReviewLogsReturnTo } from "@/utils/requestLogUrlParams";

export interface RequestLikeForAnalyticsFilters {
  prompt_id: number | null | undefined;
  prompt_name?: string | null | undefined;
  prompt_version_number: number | null | undefined;
  engine: string | null | undefined;
  tags?: string[] | null | undefined;
  tags_array?: string[] | null | undefined;
}

const uniqueNonEmptyStrings = (values: string[]): string[] => [
  ...new Set(values.map(v => v.trim()).filter(Boolean)),
];

const getPromptFilterDisplay = (
  promptId: number,
  promptVersionNumber: number | null | undefined,
  promptName?: string | null,
): string => {
  const trimmedPromptName = promptName?.trim();

  if (trimmedPromptName && typeof promptVersionNumber === "number") {
    return `${trimmedPromptName} v${promptVersionNumber}`;
  }

  if (trimmedPromptName) {
    return `${trimmedPromptName} (Any version)`;
  }

  if (typeof promptVersionNumber === "number") {
    return `Prompt #${promptId} v${promptVersionNumber}`;
  }

  return `Prompt #${promptId} (Any version)`;
};

export const buildFiltersFromRequestContext = (input: RequestLikeForAnalyticsFilters): Filter[] => {
  const tagSource = input.tags_array?.length ? input.tags_array : (input.tags ?? []);
  const uniqueTags = uniqueNonEmptyStrings(tagSource);

  const filters: Filter[] = [];

  if (typeof input.prompt_id === "number") {
    const value =
      typeof input.prompt_version_number === "number"
        ? { prompt_id: input.prompt_id, prompt_version_number: input.prompt_version_number }
        : { prompt_id: input.prompt_id };
    filters.push({
      id: crypto.randomUUID(),
      field: "prompt_id",
      type: FilterValueType.Prompt,
      operator: "is",
      value,
      valueDisplay: getPromptFilterDisplay(
        input.prompt_id,
        input.prompt_version_number,
        input.prompt_name,
      ),
    });
  }

  const engine = input.engine?.trim();
  if (engine) {
    filters.push({
      id: crypto.randomUUID(),
      field: StructuredSearchField.Model,
      type: FilterValueType.String,
      operator: "is",
      value: engine,
    });
  }

  if (uniqueTags.length > 0) {
    filters.push({
      id: crypto.randomUUID(),
      field: StructuredSearchField.Tags,
      type: FilterValueType.String,
      operator: "in",
      value: uniqueTags,
    });
  }

  return filters;
};

export const buildSharedGroupFilters = (previews: RequestLikeForAnalyticsFilters[]): Filter[] => {
  if (previews.length === 0) {
    return [];
  }

  const first = previews[0];
  const allSamePrompt =
    typeof first.prompt_id === "number" && previews.every(r => r.prompt_id === first.prompt_id);
  const allSameVersion = previews.every(
    r => r.prompt_version_number === first.prompt_version_number,
  );
  const allSameEngine =
    Boolean(first.engine?.trim()) && previews.every(r => r.engine === first.engine);

  const tagSets = previews.map(r => {
    const arr = r.tags_array?.length ? r.tags_array : (r.tags ?? []);
    return new Set(uniqueNonEmptyStrings(arr));
  });
  const sharedTags =
    tagSets.length > 0 ? [...tagSets[0]].filter(tag => tagSets.every(s => s.has(tag))) : [];

  let promptId: number | null | undefined;
  let promptVersionNumber: number | null | undefined;
  if (allSamePrompt && typeof first.prompt_id === "number") {
    promptId = first.prompt_id;
    promptVersionNumber = allSameVersion ? (first.prompt_version_number ?? undefined) : undefined;
  }

  return buildFiltersFromRequestContext({
    prompt_id: promptId ?? null,
    prompt_name: allSamePrompt ? first.prompt_name : undefined,
    prompt_version_number: promptVersionNumber ?? null,
    engine: allSameEngine ? first.engine : undefined,
    tags: sharedTags.length > 0 ? sharedTags : undefined,
  });
};

export const buildWorkspaceAnalyticsPath = ({
  workspaceId,
  baseSearch,
  filters,
  dateRange = createStructuredSearchDefaultDateRange(),
  plainSearchQuery,
  openAnalyticsTab = false,
  resetPage = true,
}: {
  workspaceId: string;
  baseSearch: string;
  filters: Filter[];
  dateRange?: CompleteDateRange;
  /** When set (including empty string), updates `q`; when omitted, leaves existing `q` in baseSearch unchanged. */
  plainSearchQuery?: string;
  openAnalyticsTab?: boolean;
  resetPage?: boolean;
}): string => {
  const stripped = stripReviewLogsReturnTo(stripReviewLogsDrawerIntent(baseSearch));
  const params = new URLSearchParams(stripped.startsWith("?") ? stripped.slice(1) : stripped);

  const next = applyStructuredSearchStateToSearchParams(
    params,
    { filters, dateRange, q: plainSearchQuery?.trim() },
    { resetPage, namespace: "requestsTable" },
  );
  const qs = next.toString();
  const route = openAnalyticsTab ? "analytics" : "request-logs";
  return `/workspace/${workspaceId}/${route}${qs ? `?${qs}` : ""}`;
};
